<template>
  <modal-vue
    :modal-active="modelValue"
    max-width="max-w-[600px]"
    :show-close-button="true"
    @close="$emit('update:modelValue', false)"
  >
    <template #header>
      <div class="flex flex-col gap-2">
        <v-icon :icon="mdiAlertCircle" size="40" color="#da1e28" />
        <h2 class="text-xl font-bold text-[#16161d] leading-6 text-left">
          Aprovar essa troca irá cancelar outros convites. Deseja continuar?
        </h2>
        <p class="text-sm text-[#52526f] leading-[18px] text-left">
          Esta ação não pode ser desfeita.
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-5">
      <div class="h-px w-full bg-[#d8d8e2]" />

      <p class="text-sm text-[#16161d] leading-[18px] text-left">
        Tem certeza que aprovar a troca de {{ stationCount }} plantões do Dr(a).
        {{ requesterName }} para o Dr(a). {{ candidateName }}? Um ou mais plantões do convite também
        estão presentes em outros convites de troca, e aprovar essa irá cancelar os outros pedidos
        <br /><br />
        Os médicos serão notificados do cancelamento
      </p>

      <div class="flex flex-col gap-2 bg-[#f0f0f4] rounded-lg p-4">
        <p
          class="text-xs font-bold text-[#52526f] uppercase tracking-wide leading-[14px] text-left"
        >
          O que será cancelado
        </p>
        <div class="flex gap-1.5 text-sm text-[#16161d] leading-[18px]">
          <span class="font-bold whitespace-nowrap">Profissional que está passando:</span>
          <span
            >{{ getFirstAndSecondName(impact.originDoctor.name) }} · CRM
            {{ impact.originDoctor.crm }}</span
          >
        </div>
        <div class="flex gap-1.5 text-sm text-[#16161d] leading-[18px]">
          <span class="font-bold whitespace-nowrap">Plantões:</span>
          <span>{{ impact.affectedShiftsDescription }}</span>
        </div>
        <div class="flex gap-1.5 text-sm text-[#16161d] leading-[18px]">
          <span class="font-bold whitespace-nowrap">Convidados com aceite ativo:</span>
          <span>{{ impact.activeAcceptanceCount }} médicos</span>
        </div>
        <div class="flex gap-1.5 text-sm text-[#16161d] leading-[18px]">
          <span class="font-bold">Profissionais que querem assumir:</span>
          <span
            >{{ impact.candidatesCount }} candidato{{
              impact.candidatesCount !== 1 ? 's' : ''
            }}</span
          >
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <v-select
          v-model="selectedReason"
          :items="reasonOptions"
          label="Motivo do cancelamento"
          variant="outlined"
          density="comfortable"
          :rules="[requiredRule]"
          hide-details="auto"
        >
          <template #label>
            Motivo do cancelamento
            <span class="text-[#da1e28]">*</span>
          </template>
        </v-select>
        <v-textarea
          v-if="selectedReason === OTHER_OPTION"
          v-model="customReason"
          variant="outlined"
          density="comfortable"
          placeholder="Digite o motivo do cancelamento..."
          :rules="[requiredRule]"
          hide-details="auto"
          rows="3"
          auto-grow
          class="mt-1"
        />
        <p class="text-xs text-[#52526f] leading-4 text-left">
          O motivo é registrado no histórico do plantão
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col gap-4">
        <div
          v-if="hasScheduleConflicts"
          data-testid="schedule-conflict-alert"
          class="flex flex-col gap-2 rounded-lg p-3 bg-[#fdecec] border border-[#da1e28] text-left"
        >
          <p class="text-sm text-[#16161d] leading-[18px]">
            <span class="font-bold">Conflito de agenda:</span> Dr(a).
            {{ scheduleConflicts?.doctorName }} já está alocado(a) em
            {{ scheduleConflicts?.conflicts.length }} plantão(ões) que se sobrepõe(m) a este
            convite:
          </p>
          <ul class="flex flex-col gap-1">
            <li
              v-for="(conflict, index) in scheduleConflicts?.conflicts"
              :key="index"
              class="text-sm text-[#16161d] leading-[18px]"
            >
              • {{ formatConflictDate(conflict.date) }} · {{ conflict.startHour }}—{{
                conflict.endHour
              }}
              · {{ conflict.healthUnitName }} — {{ conflict.scheduleName }}
            </li>
          </ul>
        </div>

        <div
          data-testid="approval-reminder-callout"
          class="flex items-start gap-2 rounded-lg p-3 bg-[#edf5ff] border border-[#0f62fe]"
        >
          <v-icon :icon="mdiInformationOutline" size="18" color="#0f62fe" class="shrink-0 mt-0.5" />
          <p class="text-sm text-[#16161d] leading-[18px] text-left">
            Ao aprovar, você confirma a autorização do profissional para este plantão e garante a
            atualização no sistema ou planilha da contratante.
          </p>
        </div>

        <div class="flex items-center justify-between">
          <OutlineButton
            text="Cancelar aprovação"
            width="auto"
            height="48px"
            :disabled="loading"
            @click="$emit('update:modelValue', false)"
          />
          <v-btn
            color="#da1e28"
            variant="flat"
            height="48"
            class="normal-case font-bold text-sm text-white rounded px-4"
            :disabled="!isFormValid || loading"
            :loading="loading"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </v-btn>
        </div>
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { format, parseISO } from 'date-fns'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  PackageResponse,
  InviteResponseV2,
  DoctorSummary,
  ScheduleConflictsSummary
} from '@/Network/Types/Responses/HandoverInviteResponses'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { mdiAlertCircle, mdiInformationOutline } from '@mdi/js'

interface KillSwitchImpact {
  hasConflicts: boolean
  originDoctor: DoctorSummary
  affectedShiftsCount: number
  affectedShiftsDescription: string
  activeAcceptanceCount: number
  candidatesCount: number
}

const OTHER_OPTION = 'Outro (especificar)'

const reasonOptions = [
  'Ajuste operacional da escala',
  'Erro na configuração do plantão',
  'Solicitação do médico responsável',
  'Mudança de requisito da unidade',
  OTHER_OPTION
]

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    packageData: PackageResponse
    candidate: InviteResponseV2
    impact: KillSwitchImpact
    loading: boolean
    confirmText?: string
    scheduleConflicts?: ScheduleConflictsSummary | null
  }>(),
  {
    confirmText: 'Cancelar trocas e aprovar convite'
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [reason: string]
}>()

const selectedReason = ref<string | null>(null)
const customReason = ref('')

const requiredRule = (v: string) => !!v || 'Campo obrigatório'

const stationCount = computed(() => props.packageData.stations.length)
const requesterName = computed(() => getFirstAndSecondName(props.packageData.requester.name))
const candidateName = computed(() => getFirstAndSecondName(props.candidate.doctor?.name ?? ''))

const hasScheduleConflicts = computed(
  () => !!props.scheduleConflicts && props.scheduleConflicts.conflicts.length > 0
)

const formatConflictDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy')
  } catch {
    return dateStr
  }
}

const finalReason = computed(() => {
  if (selectedReason.value === OTHER_OPTION) return customReason.value.trim()
  return selectedReason.value ?? ''
})

const isFormValid = computed(() => {
  if (!selectedReason.value) return false
  if (selectedReason.value === OTHER_OPTION && !customReason.value.trim()) return false
  return true
})

const handleConfirm = () => {
  if (!isFormValid.value) return
  emit('confirm', finalReason.value)
}
</script>

<style scoped>
:deep(header) {
  padding-bottom: 0 !important;
}
</style>
